import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
class InvoiceBatch extends Base implements EntityType {
    invoiceBatchId: uuid;
    startDate?: Date | null;
    endDate: Date;
    transferDate?: Date | null;
    invoiceIds: uuid[];
    invoiceBatchItemIds: uuid[];
    constructor(doc: typeof InvoiceBatch | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            invoiceBatchId: genUUID(),
            endDate: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "invoiceBatch";
        // @ts-ignore
        this.invoiceBatchId = doc._invoiceBatchId || doc.invoiceBatchId || doc.invoicebatchid || genUUID();
        // @ts-ignore
        this.startDate = DateUtil.dateOnly(doc._startDate) || DateUtil.dateOnly(doc.startDate) || DateUtil.dateOnly(doc.startdate) || null;
        // @ts-ignore
        this.endDate = DateUtil.dateOnly(doc._endDate) || DateUtil.dateOnly(doc.endDate) || DateUtil.dateOnly(doc.enddate) || null;
        // @ts-ignore
        this.transferDate = DateUtil.dateOnly(doc._transferDate) || DateUtil.dateOnly(doc.transferDate) || DateUtil.dateOnly(doc.transferdate) || null;
        // @ts-ignore
        this.invoiceIds = doc._invoiceIds || doc.invoiceIds || doc.invoiceids || [];
        // @ts-ignore
        this.invoiceBatchItemIds = doc._invoiceBatchItemIds || doc.invoiceBatchItemIds || doc.invoicebatchitemids || [];
    }
    get id(): uuid { return this.invoiceBatchId; }
    addInvoiceId(invoiceId: uuid): uuid {
        if (!invoiceId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.invoiceIds) this.invoiceIds = [];
        this.invoiceIds.push(invoiceId);
        return invoiceId;
    }
    updateInvoiceId(invoiceId: uuid) {
        if (!this.invoiceIds) this.invoiceIds = [];
        const index = this.invoiceIds.findIndex(item => item === invoiceId);
        if (index !== -1) { this.invoiceIds[index] = invoiceId; }
        else this.addInvoiceId(invoiceId);
    }
    removeInvoiceId(invoiceId: uuid) {
        if (!this.invoiceIds) return;
        let foundItems = this.invoiceIds?.filter(item => item === invoiceId);
        if (foundItems && foundItems.length > 0) {
            this.invoiceIds = this.invoiceIds.filter(item => !(item === invoiceId));
        }
    }
    abandonInvoiceIds() { this.invoiceIds = []; }
    addInvoiceBatchItemId(invoiceBatchItemId: uuid): uuid {
        if (!invoiceBatchItemId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.invoiceBatchItemIds) this.invoiceBatchItemIds = [];
        this.invoiceBatchItemIds.push(invoiceBatchItemId);
        return invoiceBatchItemId;
    }
    updateInvoiceBatchItemId(invoiceBatchItemId: uuid) {
        if (!this.invoiceBatchItemIds) this.invoiceBatchItemIds = [];
        const index = this.invoiceBatchItemIds.findIndex(item => item === invoiceBatchItemId);
        if (index !== -1) { this.invoiceBatchItemIds[index] = invoiceBatchItemId; }
        else this.addInvoiceBatchItemId(invoiceBatchItemId);
    }
    removeInvoiceBatchItemId(invoiceBatchItemId: uuid) {
        if (!this.invoiceBatchItemIds) return;
        let foundItems = this.invoiceBatchItemIds?.filter(item => item === invoiceBatchItemId);
        if (foundItems && foundItems.length > 0) {
            this.invoiceBatchItemIds = this.invoiceBatchItemIds.filter(item => !(item === invoiceBatchItemId));
        }
    }
    abandonInvoiceBatchItemIds() { this.invoiceBatchItemIds = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this invoiceBatch"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.invoiceBatchId; }
    static override getNonRefArrayFields(): string[] { return ["invoiceIds", "invoiceBatchItemIds"]; }
    static override getDefaultEntries(): InvoiceBatch[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'invoiceBatch',
    dataArea: 'Billing',
    table: 'INVOICE_BATCH',
    comment: 'A collection of finalized invoices grouped by billing period, used to manage post-invoicing accounting tasks for a law firm. Tracks trust accounting (IOLTA replenishment status and amounts to transfer from trust to operating), invoice delivery (sent date and method), and is considered complete when a transferDate is recorded.',
    fields: {
        invoiceBatchId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this invoiceBatch'
        },

        /**
         * Beginning date for invoice batch (using issueDates).
         */
        startDate: {
            type: 'date', comment: 'Start date for activities/expenses included in this invoiceBatch'
        },

        /**
         * Ending date for invoice batch (using issueDates).
         */
        endDate: {
            type: 'date', required: true, comment: 'End date for activities/expenses included in this invoiceBatch'
        },

        /**
         * Date that money was transferred from trust to operating account.
         */
        transferDate: {
            type: 'date', comment: 'Date that the transfer of money was completed'
        },

        /**
         * Invoice IDs in this batch
         */
        invoiceIds: {
            type: 'uuid[]', entityType: 'invoice', comment: 'ID of each invoice included in this batch'
        },

        /**
         * InvoiceBatchItems in this batch
         */
        invoiceBatchItemIds: {
            type: 'uuid[]', entityType: 'invoiceBatchItem', comment: 'Related invoice batch items'
        }

    },
    indexes: [
        {name: 'notDeleted', fn: a => a.status !== 'D'}
    ],
    maps: [
        {name: 'invoiceId', keysFn: t => t.invoiceIds, presentFn: (t, k) => t.invoiceIds?.includes(k)},
        {name: 'invoiceBatchItemId', keysFn: t => t.invoiceBatchItemIds, presentFn: (t, k) => t.invoiceBatchItemIds?.includes(k)},
    ]
});
    }
}
export default InvoiceBatch;
